import React, { useContext, useState, useEffect, useRef } from "react";
import { Toast } from "src/components";
import SendIcon from "src/img/new/send.svg";
import AttachIcon from "src/img/new/attach.svg";
import { ChatContext } from "src/scenes/Chat/ChatContext";
import {
    ChatTextBox, ChatInput, SendIconContainer, 
    AttachFileContainer, ChatBoxContainer
} from "src/scenes/Chat/components/styled";
import { MatrixError } from "matrix-js-sdk";
import { useFilePreviews } from "../hooks/chat/useFilePreview";
import { ReplyContainer, EditContainer, AttachmentContainer } from "./children/chatInput";
import { handlePaste } from "../utils";

const ChatInputMessages = (props) => {
    const fileInputRef = useRef(null);
    const textInputRef = useRef(null);
    const { currentChat, onDragPreviews, onDragSetPreviews } = props;
    const { 
        matrixClient,
        useMatrixChatList: ctxUseMatrixChatList,
        useSendMessage: ctxUseSendMessage
    } = useContext(ChatContext);

    const { upsertRoomInMatrixChatList } = ctxUseMatrixChatList;
    
    const { 
        prepareMessage, 
        newMessage, 
        setNewMessage, 
        queued, 
        attachments,
        newMessageInReplyToEvent, 
        onSetNewMessageInReplyToEventHandler,
        editMessageEvent,
        onSetEditMessageEventHandler,
        onResetInputMessageEvents,
        setAttachments,
    } = ctxUseSendMessage;
    
    const [isLocalTypingMessage, setIsLocalTypingMessage] = useState(newMessage);
    const [isLocalTypingState, setIsLocalTypingState] = useState(null);
    const [hasReply, setHasReply] = useState(false);
    const { previews, setPreviews, handleAttachFile } = useFilePreviews();
    const messageChecker = !newMessage?.trim() && attachments?.length === 0;

    // Combine attachment from drag and from uplaod
    useEffect(() => {
        const combined = [...previews, ...onDragPreviews];
        const unique = combined.filter(
            (file, i, self) =>
                self.findIndex(f =>
                    f.name === file.name &&
                    f.type === file.type &&
                    f.file?.lastModified === file.file?.lastModified
                ) === i
        );
        setAttachments(unique.map(file => ({
            ...file,
            file: new File([file.file], file.name, { type: file.type }),
            loading: false
        })));

    }, [previews, onDragPreviews]);
    
    useEffect(() => {
        textInputRef.current.focus();
        if (!newMessageInReplyToEvent || currentChat.matrixRoomId !== newMessageInReplyToEvent.getRoomId()) {
            setHasReply(false);
        } else {
            setHasReply(true);
        }
    }, [newMessageInReplyToEvent, currentChat]);

    useEffect(() => {
        textInputRef.current.focus();
        if (!editMessageEvent) return;

        if (currentChat.matrixRoomId !== editMessageEvent.getRoomId()) {
            onSetEditMessageEventHandler(null);
            return;
        }
        setNewMessage(editMessageEvent?.getContent().body);
    }, [editMessageEvent, currentChat]);

    const handleKeyPress = (e) => {
        const textarea = textInputRef.current;

        if (e.key === "Enter" && e.shiftKey) {
            e.preventDefault();
            setNewMessage(prev => (prev || "") + "\n");

            if (textarea) {
                textarea.style.paddingBottom = "0.75rem";
                textarea.style.height = "auto";
                textarea.style.height = `${textarea.scrollHeight}px`;
            }
        } else if (e.key === "Enter" && !e.shiftKey && !messageChecker) {
            e.preventDefault();
            handleSend();
            if (textarea) {
                textarea.style.paddingBottom = "";
                textarea.style.height = "auto";
            }
        }
    };

    const handleSend = () => {
        setIsLocalTypingState(null);
        if (queued?.isSending) return;
        prepareMessage();
        upsertRoomInMatrixChatList(currentChat.matrixRoomId);
        setPreviews([]);
        onDragSetPreviews([]);
    };

    const handleFileSelection = (event) => {
        handleAttachFile(event.target.files);
        event.target.value = "";
    };

    const setNewMessageAndTriggerTypingEvent = (value) => {
        setIsLocalTypingState(value);
        setNewMessage(value);

        if (isLocalTypingMessage !== value) {
            handleSendTypingEvent(true);
        }
    };
    
    const handleSendTypingEvent = async (value) => {
        try {
            if (isLocalTypingState === value || isLocalTypingMessage === newMessage) return;
            setIsLocalTypingMessage(newMessage);

            await matrixClient.sendTyping(currentChat.matrixRoomId, value, 200);
            upsertRoomInMatrixChatList(currentChat.matrixRoomId);
        } catch (error) {
            console.error("handleSendTypingEvent encountered an error: ", error);
            const matrixError = new MatrixError(error);
            /**
             * this error means the logged-in user is sending a `typing` event to a room he/she is not a member of;
             * - a deleted chat, or one that they have been removed
             * ChatList should prevent this from happening see `matrixRoomIdToRemove` effect
             * in case that it doesn't, this will make the error somehow friendly :)
             */
            if (matrixError.errcode === "M_FORBIDDEN") {
                Toast.error("You can no longer send messages in this room. Page will reload.");
                setTimeout(() => window.location.reload(), 1500);
            }
        }
    };

    useEffect(() => {
        const textarea = textInputRef.current;
        if (!textarea) return;

        textarea.style.height = "auto";

        textarea.style.height = `${textarea.scrollHeight}px`;
    }, [newMessage]);

    return (
        <>
            { hasReply && newMessageInReplyToEvent &&
                <ReplyContainer 
                    newMessageInReplyToEvent={newMessageInReplyToEvent}
                    onSetNewMessageThreadEvent={onSetNewMessageInReplyToEventHandler}
                    currentChat={currentChat}
                    textInputRef={textInputRef}
                    matrixClient={matrixClient}
                />
            }

            { editMessageEvent &&
                <EditContainer 
                    editMessageEvent={editMessageEvent}
                    onSetEditMessageEventHandler={onSetEditMessageEventHandler}
                    textInputRef={textInputRef}
                    matrixClient={matrixClient}
                />
            }

            {attachments && (
                <AttachmentContainer
                    attachments={attachments}
                    onRemove={(idx) => {
                        if (idx === -1) {
                            setPreviews([]);
                            onDragSetPreviews([]);
                            onResetInputMessageEvents([]);
                        } else {
                            const toRemove = attachments[idx];
                            setPreviews(prev => prev.filter(f => !(f.name === toRemove.name && f.type === toRemove.type)));
                            onDragSetPreviews(prev => prev.filter(f => !(f.name === toRemove.name && f.type === toRemove.type)));
                            onResetInputMessageEvents([]);
                        }
                    }}
                />
            )}

            <ChatBoxContainer>
                <div style={{ padding: "0 1rem" }}>
                    <ChatTextBox onKeyDown={handleKeyPress}>
                        <AttachFileContainer
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <img src={AttachIcon} alt="Attachment" />
                        </AttachFileContainer>
                        <input ref={fileInputRef} type="file" multiple hidden onChange={handleFileSelection} />

                        <ChatInput
                            ref={textInputRef}
                            placeholder="Enter your message here"
                            onChange={(e) => setNewMessageAndTriggerTypingEvent(e.target.value)}
                            value={newMessage || ""}
                            onFocus={() => handleSendTypingEvent(true)}
                            onBlur={() => handleSendTypingEvent(false)}
                            onPaste={(event) => handlePaste(event, setAttachments)} 
                        />

                        <SendIconContainer onClick={handleSend}
                            disabled={messageChecker}
                            isSending={queued !== null && queued.isSending}
                        >
                            <img src={SendIcon} alt="Send" style={{ transform: "rotate(-90deg)" }}/>
                        </SendIconContainer>
                    </ChatTextBox>
                </div>
            </ChatBoxContainer>
        </>
    );
};

export default ChatInputMessages;
